import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Endpoint } from '@/Config/api/endpoint';
import { SubscriptionApi } from '@/Domain/api/Subscription/Subscription.api';

type Props = {
  cancelCallback: () => void;
  activeCallback: () => void;
};

const TEN_SECONDS = 10000;
export const useChangeSubscriptionStatus = ({ activeCallback, cancelCallback }: Props) => {
  const queryClient = useQueryClient();

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const mutation = useMutation({
    mutationKey: [Endpoint.subscription.cancel],
    mutationFn: async ({
      status,
      cancellationReason,
    }: {
      status: 'activate' | 'cancel';
      cancellationReason?: string;
    }) => SubscriptionApi.cancel(status, cancellationReason),
    onSuccess: (_, variables) => {
      timeoutRef.current = setTimeout(() => {
        queryClient.refetchQueries({ queryKey: [Endpoint.subscription.get] }).then(() => {
          if (variables.status === 'activate') {
            return activeCallback();
          }

          return cancelCallback();
        });
      }, TEN_SECONDS);
    },
  });

  const handleChangeSubscriptionStatus = (
    status: 'activate' | 'cancel',
    cancellationReason?: string,
  ) => mutation.mutateAsync({ status, cancellationReason });

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    [],
  );

  return {
    handleChangeSubscriptionStatus,
    isPending: mutation.isPending,
  };
};
